import Vue from 'vue';
import Swiper, { Navigation } from 'swiper';

Swiper.use([Navigation]);

Vue.component("availability-component", {
    props: {
        chapters: {
            type: Array,
            default: () => []
        },
        items: {
            type: Array,
            required: true
        },
        mapCenterLat: Number,
        mapCenterLong: Number,
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            swipers: [],
            selectedItemsSwiper: null,
            map: null,
            markers: [],
            markerListeners: [],
            mapClickListener: null,
            markerByKey: {},
            itemsByMarkerKey: {},
            markerMetaByKey: {},
            activeMarkerKey: null,
            activeView: 'list',
            selectedMarkerItems: [],
            selectedMarkerNoAvailabilityMessage: '',
            isMobileViewport: false,
            viewportWidth: window.innerWidth,
            filteredMarkerKey: null,
            filteredDesktopItems: [],
            filteredDesktopNoAvailabilityMessage: '',
            lightboxOpen: false,
            lightboxImages: [],
            lightboxIndex: 0,
            lightboxSwiper: null
        };
    },

    computed: {
        visibleItems() {
            if (!this.isMobileViewport && this.filteredMarkerKey) {
                return this.filteredDesktopItems;
            }

            return this.items;
        },

        showResetFilter() {
            return !this.isMobileViewport && !!this.filteredMarkerKey;
        },

        shouldUseSelectedItemsSwiper() {
            if (!this.isMobileViewport) {
                return false;
            }

            const slidesPerView = this.viewportWidth >= 768 ? 2.2 : 1.2;
            return this.selectedMarkerItems.length > slidesPerView;
        },

        hasSelectedMarkerOverlay() {
            return this.isMobileViewport && (this.selectedMarkerItems.length || !!this.selectedMarkerNoAvailabilityMessage);
        }
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.updateViewportState();
        window.addEventListener('resize', this.onWindowResize);
        document.addEventListener('keydown', this.onLightboxKeydown);

        this.init();

        this.$nextTick().then(() => {
            this.initSwipers();
        });
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
        document.removeEventListener('keydown', this.onLightboxKeydown);

        this.closeLightbox();

        this.swipers.forEach((swiper) => {
            if (swiper && !swiper.destroyed) {
                swiper.destroy(true, true);
            }
        });
        this.swipers = [];

        if (this.selectedItemsSwiper && !this.selectedItemsSwiper.destroyed) {
            this.selectedItemsSwiper.destroy(true, true);
        }
        this.selectedItemsSwiper = null;

        this.markers.forEach((marker) => {
            marker.setMap(null);
        });
        this.markers = [];
        this.markerListeners.forEach((listener) => {
            if (listener && typeof listener.remove === 'function') {
                listener.remove();
            }
        });
        this.markerListeners = [];
        if (this.mapClickListener && typeof this.mapClickListener.remove === 'function') {
            this.mapClickListener.remove();
        }
        this.mapClickListener = null;
        this.markerByKey = {};
        this.itemsByMarkerKey = {};
        this.markerMetaByKey = {};
        this.activeMarkerKey = null;
        this.selectedMarkerItems = [];
        this.selectedMarkerNoAvailabilityMessage = '';
        this.filteredMarkerKey = null;
        this.filteredDesktopItems = [];
        this.filteredDesktopNoAvailabilityMessage = '';
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        updateViewportState() {
            this.viewportWidth = window.innerWidth;
            this.isMobileViewport = this.viewportWidth < 1024;
        },

        onWindowResize() {
            const wasMobileViewport = this.isMobileViewport;
            this.updateViewportState();

            if (wasMobileViewport && !this.isMobileViewport) {
                this.clearSelectedMarkerItems();
            }
        },

        clearSelectedMarkerItems() {
            this.selectedMarkerItems = [];
            this.selectedMarkerNoAvailabilityMessage = '';

            if (this.selectedItemsSwiper && !this.selectedItemsSwiper.destroyed) {
                this.selectedItemsSwiper.destroy(true, true);
            }
            this.selectedItemsSwiper = null;
        },

        initSelectedItemsSwiper() {
            if (!this.isMobileViewport || !this.selectedMarkerItems.length) {
                return;
            }

            this.$nextTick(() => {
                this.initSwipers();

                const overlaySwiperElement = this.$refs.selectedItemsSwiper;

                if (!overlaySwiperElement) {
                    return;
                }

                const overlayContainer = overlaySwiperElement.closest('.availability-map__selected-item-overlay') || this.$el;
                const nextButton = overlayContainer
                    ? overlayContainer.querySelector('.availability-map__overlay-navigation-button--next')
                    : null;
                const prevButton = overlayContainer
                    ? overlayContainer.querySelector('.availability-map__overlay-navigation-button--prev')
                    : null;

                if (this.selectedItemsSwiper && !this.selectedItemsSwiper.destroyed) {
                    this.selectedItemsSwiper.destroy(true, true);
                }

                if (!this.shouldUseSelectedItemsSwiper) {
                    this.selectedItemsSwiper = null;
                    return;
                }

                this.selectedItemsSwiper = new Swiper(overlaySwiperElement, {
                    modules: [Navigation],
                    direction: 'horizontal',
                    loop: false,
                    navigation: {
                        nextEl: nextButton,
                        prevEl: prevButton,
                    },
                    slidesPerView: 1.2,
                    spaceBetween: 10,
                    breakpoints: {
                        768: {
                            slidesPerView: 2.2,
                            spaceBetween: 20,
                        }
                    }
                });
            });
        },

        navigateSelectedItems(direction) {
            if (!this.selectedItemsSwiper || this.selectedItemsSwiper.destroyed) {
                return;
            }

            if (direction === 'next') {
                this.selectedItemsSwiper.slideNext();
                return;
            }

            if (direction === 'prev') {
                this.selectedItemsSwiper.slidePrev();
            }
        },

        resetFilter() {
            this.filteredMarkerKey = null;
            this.filteredDesktopItems = [];
            this.filteredDesktopNoAvailabilityMessage = '';
            this.resetMarkerOpacity();

            if (!this.isMobileViewport) {
                this.refreshItemSwipers(true);
            }
        },

        refreshItemSwipers(shouldAnimateItems = false) {
            this.swipers.forEach((swiper) => {
                if (swiper && !swiper.destroyed) {
                    swiper.destroy(true, true);
                }
            });
            this.swipers = [];

            this.$nextTick(() => {
                this.initSwipers();

                if (shouldAnimateItems) {
                    this.animateDesktopItems();
                }
            });
        },

        animateDesktopItems() {
            if (this.isMobileViewport || !this.$el) {
                return;
            }

            const items = this.$el.querySelectorAll('.availability-items .availability-item');

            if (!items.length) {
                return;
            }

            items.forEach((item) => {
                item.classList.remove('availability-item--animate-in');
                item.classList.remove('availability-item--pre-animate');
                item.style.removeProperty('--availability-item-stagger');
            });

            window.requestAnimationFrame(() => {
                items.forEach((item, index) => {
                    item.classList.add('availability-item--pre-animate');
                    item.style.setProperty('--availability-item-stagger', `${Math.min(index * 70, 560)}ms`);
                });

                window.requestAnimationFrame(() => {
                    items.forEach((item) => {
                        item.classList.remove('availability-item--pre-animate');
                        item.classList.add('availability-item--animate-in');
                    });
                });
            });
        },

        setMarkerOpacityForDesktopFilter(activeMarkerKey) {
            if (this.isMobileViewport || !activeMarkerKey) {
                return;
            }

            this.markers.forEach((marker) => {
                if (!marker || typeof marker.setOpacity !== 'function') {
                    return;
                }

                const markerKey = marker.availabilityMarkerKey;
                marker.setOpacity(markerKey === activeMarkerKey ? 1 : 0.5);
            });
        },

        resetMarkerOpacity() {
            this.markers.forEach((marker) => {
                if (marker && typeof marker.setOpacity === 'function') {
                    marker.setOpacity(1);
                }
            });
        },

        onMarkerClick(markerKey) {
            const markerItems = (this.itemsByMarkerKey[markerKey] || []).slice();
            const markerMeta = this.markerMetaByKey[markerKey] || {};
            const chapterLabel = markerMeta.chapterNumberLabel || markerMeta.chapterTitle || 'this chapter';
            const noAvailabilityMessage = chapterLabel === 'this chapter'
                ? 'There is currently no space available in this chapter.'
                : `There is currently no space available in Chapter ${chapterLabel}.`;

            if (!this.isMobileViewport) {
                this.filteredMarkerKey = markerKey;
                this.filteredDesktopItems = markerItems;
                this.filteredDesktopNoAvailabilityMessage = markerItems.length
                    ? ''
                    : noAvailabilityMessage;
                this.setMarkerOpacityForDesktopFilter(markerKey);
                this.refreshItemSwipers(true);
                return;
            }

            this.selectedMarkerItems = markerItems;
            this.selectedMarkerNoAvailabilityMessage = markerItems.length
                ? ''
                : noAvailabilityMessage;
            this.initSelectedItemsSwiper();
        },

        normalizeChapterKey(value) {
            if (value === undefined || value === null) {
                return null;
            }

            return String(value).trim().toLowerCase();
        },

        getChapterMarkerKey(chapter) {
            if (!chapter) {
                return null;
            }

            const chapterId = this.normalizeChapterKey(chapter.id);

            if (chapterId) {
                return `chapter-id-${chapterId}`;
            }

            const chapterTitle = this.normalizeChapterKey(chapter.title);

            if (chapterTitle) {
                return `chapter-title-${chapterTitle}`;
            }

            return null;
        },

        getMarkerKey(item) {
            if (!item) {
                return null;
            }

            const chapterId = this.normalizeChapterKey(item.chapterId);

            if (chapterId) {
                return `chapter-id-${chapterId}`;
            }

            const chapterTitle = this.normalizeChapterKey(item.chapter);

            if (chapterTitle) {
                return `chapter-title-${chapterTitle}`;
            }

            return null;
        },

        getChapterNumberLabel(chapter) {
            if (!chapter || !chapter.title) {
                return '';
            }

            const title = String(chapter.title).trim();

            if (!title) {
                return '';
            }

            return title.replace(/^chapter\s*/i, '').trim();
        },

        /**
         * Builds the DOM for a single map marker by cloning the template that
         * ships with the page, so the hover state can reuse the same markup and
         * styling as the chapters map.
         *
         * @return {HTMLElement|null}
         */
        buildMarkerElement(hasAvailability, labelText) {
            const template = this.$refs.markerTemplate || this.$el.querySelector('.availability-map__marker-template');
            const source = template ? template.querySelector('.availability-map__point') : null;

            if (!source) {
                return null;
            }

            const element = source.cloneNode(true);

            if (hasAvailability) {
                element.classList.add('availability-map__point--has-availability');
            }

            const label = element.querySelector('.availability-map__point-label');

            if (label) {
                label.textContent = hasAvailability && labelText ? String(labelText) : '';
            }

            return element;
        },

        /**
         * Defines the custom marker overlay. Can only run once the Maps API has
         * loaded, because google.maps.OverlayView does not exist before then.
         *
         * @return {Function}
         */
        createMarkerOverlayClass() {
            const component = this;

            return class AvailabilityMarkerOverlay extends google.maps.OverlayView {
                constructor(options) {
                    super();

                    this.position = options.position;
                    this.availabilityMarkerKey = options.markerKey;
                    this.hasAvailability = options.hasAvailability;
                    this.labelText = options.label;
                    this.titleText = options.title;
                    this.element = null;
                    this.opacity = 1;
                }

                onAdd() {
                    const element = component.buildMarkerElement(this.hasAvailability, this.labelText);

                    if (!element) {
                        return;
                    }

                    if (this.titleText) {
                        element.setAttribute('title', this.titleText);
                    }

                    element.style.opacity = this.opacity;

                    element.addEventListener('click', (event) => {
                        event.stopPropagation();
                        google.maps.event.trigger(this, 'click');
                    });

                    this.element = element;
                    this.getPanes().overlayMouseTarget.appendChild(element);
                }

                draw() {
                    if (!this.element) {
                        return;
                    }

                    const projection = this.getProjection();
                    const point = projection ? projection.fromLatLngToDivPixel(this.position) : null;

                    if (!point) {
                        return;
                    }

                    this.element.style.left = `${point.x}px`;
                    this.element.style.top = `${point.y}px`;
                }

                onRemove() {
                    if (this.element && this.element.parentNode) {
                        this.element.parentNode.removeChild(this.element);
                    }

                    this.element = null;
                }

                setOpacity(opacity) {
                    this.opacity = opacity;

                    if (this.element) {
                        this.element.style.opacity = opacity;
                    }
                }
            };
        },

        onItemMouseEnter(item) {
            if (!this.map || !window.google || !google.maps) {
                return;
            }

            const markerKey = this.getMarkerKey(item);

            if (!markerKey || !this.markerByKey[markerKey]) {
                return;
            }

            this.activeMarkerKey = markerKey;

            this.markers.forEach((marker) => {
                if (!marker || typeof marker.setOpacity !== 'function') {
                    return;
                }

                const key = marker.availabilityMarkerKey;
                marker.setOpacity(key === markerKey ? 1 : 0.5);
            });
        },

        onItemMouseLeave(item) {
            const markerKey = this.getMarkerKey(item);

            if (!markerKey || !this.markerByKey[markerKey]) {
                return;
            }

            if (this.activeMarkerKey === markerKey) {
                this.activeMarkerKey = null;
            }

            this.resetMarkerOpacity();
        },

        initSwipers() {
            const items = this.$el.querySelectorAll('.availability-item');

            items.forEach((item) => {
                const sliderHolder = item.querySelector('.availability-item__images');
                const nextButton = item.querySelector('.availability-item__slider-nav-button--next');
                const prevButton = item.querySelector('.availability-item__slider-nav-button--prev');

                if (!sliderHolder || !nextButton || !prevButton) {
                    return;
                }

                if (sliderHolder.classList.contains('swiper-initialized')) {
                    return;
                }

                const swiper = new Swiper(sliderHolder, {
                    modules: [Navigation],
                    direction: 'horizontal',
                    loop: true,
                    navigation: {
                        nextEl: nextButton,
                        prevEl: prevButton,
                    },
                    slidesPerView: 1,
                    spaceBetween: 0,
                });

                const itemData = this.getItemByElement(item);

                if (itemData) {
                    // Swiper only emits 'click' for genuine clicks (never after a
                    // drag), and it fires for looped clones too, which a Vue
                    // @click on the slide would not.
                    swiper.on('click', () => {
                        this.openLightbox(itemData, this.getClickedSlideIndex(swiper));
                    });
                }

                this.swipers.push(swiper);
            });
        },

        /**
         * Resolves the item data behind an .availability-item element.
         *
         * @return {Object|null}
         */
        getItemByElement(itemElement) {
            const itemId = itemElement ? itemElement.dataset.itemId : null;

            if (!itemId) {
                return null;
            }

            return this.items.find((item) => String(item.id) === String(itemId)) || null;
        },

        /**
         * Returns the real index of the clicked slide, accounting for the
         * duplicate slides that loop mode adds.
         *
         * @return {Number}
         */
        getClickedSlideIndex(swiper) {
            const clickedSlide = swiper ? swiper.clickedSlide : null;

            if (clickedSlide && clickedSlide.dataset.swiperSlideIndex !== undefined) {
                const slideIndex = parseInt(clickedSlide.dataset.swiperSlideIndex, 10);

                if (Number.isInteger(slideIndex)) {
                    return slideIndex;
                }
            }

            return swiper && Number.isInteger(swiper.realIndex) ? swiper.realIndex : 0;
        },

        openLightbox(item, imageIndex = 0) {
            if (!item || !item.images || !item.images.length) {
                return;
            }

            this.lightboxImages = item.images;
            this.lightboxIndex = Math.min(Math.max(imageIndex, 0), item.images.length - 1);
            this.lightboxOpen = true;
            document.body.classList.add('availability-lightbox-is-open');

            this.$nextTick(() => {
                this.initLightboxSwiper();
            });
        },

        closeLightbox() {
            if (this.lightboxSwiper && !this.lightboxSwiper.destroyed) {
                this.lightboxSwiper.destroy(true, true);
            }

            this.lightboxSwiper = null;
            this.lightboxOpen = false;
            this.lightboxImages = [];
            this.lightboxIndex = 0;
            document.body.classList.remove('availability-lightbox-is-open');
        },

        initLightboxSwiper() {
            const lightboxElement = this.$refs.lightboxSwiper;

            if (!lightboxElement) {
                return;
            }

            if (this.lightboxSwiper && !this.lightboxSwiper.destroyed) {
                this.lightboxSwiper.destroy(true, true);
            }

            const hasMultipleImages = this.lightboxImages.length > 1;

            this.lightboxSwiper = new Swiper(lightboxElement, {
                modules: [Navigation],
                direction: 'horizontal',
                loop: hasMultipleImages,
                initialSlide: this.lightboxIndex,
                navigation: hasMultipleImages
                    ? {
                        nextEl: this.$refs.lightboxNext,
                        prevEl: this.$refs.lightboxPrev,
                    }
                    : false,
                slidesPerView: 1,
                spaceBetween: 0,
            });

            this.lightboxSwiper.on('slideChange', () => {
                this.lightboxIndex = this.lightboxSwiper.realIndex;
            });
        },

        onLightboxKeydown(event) {
            if (!this.lightboxOpen) {
                return;
            }

            if (event.key === 'Escape') {
                this.closeLightbox();
                return;
            }

            if (!this.lightboxSwiper || this.lightboxSwiper.destroyed) {
                return;
            }

            if (event.key === 'ArrowLeft') {
                this.lightboxSwiper.slidePrev();
            } else if (event.key === 'ArrowRight') {
                this.lightboxSwiper.slideNext();
            }
        },

        init() {
            EventBus.$once("google-maps-ready", this.initMap);

            this.intersect();
        },

        /**
         * Initialize the Google Map
         *
         * @return {Void}
         */
        initMap() {
            if (this.map) {
                return;
            }

            const mapElement = document.getElementById("gmap") || document.getElementById("availability-map-container");

            if (!mapElement) {
                return;
            }

            const mapStyle = [
                {
                    "featureType": "all",
                    "elementType": "labels.text.fill",
                    "stylers": [
                        {
                            "saturation": "0"
                        },
                        {
                            "lightness": "28"
                        },
                        {
                            "color": "#545456"
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "all",
                    "elementType": "labels.text.stroke",
                    "stylers": [
                        {
                            "visibility": "on"
                        },
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "-24"
                        },
                        {
                            "saturation": "0"
                        },
                        {
                            "gamma": "1"
                        }
                    ]
                },
                {
                    "featureType": "all",
                    "elementType": "labels.icon",
                    "stylers": [
                        {
                            "visibility": "off"
                        }
                    ]
                },
                {
                    "featureType": "administrative",
                    "elementType": "geometry.fill",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "-5"
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "administrative",
                    "elementType": "geometry.stroke",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "15"
                        },
                        {
                            "weight": 1.2
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "landscape",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "0"
                        },
                        {
                            "gamma": "0.95"
                        },
                        {
                            "saturation": "0"
                        }
                    ]
                },
                {
                    "featureType": "poi",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "-10"
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "road.highway",
                    "elementType": "geometry.fill",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "5"
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "road.highway",
                    "elementType": "geometry.stroke",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "10"
                        },
                        {
                            "weight": 0.2
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "road.arterial",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "0"
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "road.local",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "-93"
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "transit",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#1d1e21"
                        },
                        {
                            "lightness": "15"
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                },
                {
                    "featureType": "water",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#000000"
                        },
                        {
                            "lightness": "-5"
                        },
                        {
                            "gamma": "1.00"
                        }
                    ]
                }
            ];

            this.map = new google.maps.Map(mapElement, {
                zoom: 14,
                center: { lat: this.mapCenterLat, lng: this.mapCenterLong },
                styles: mapStyle,
                scrollwheel: false,
                streetViewControl: false,
                disableDefaultUI: true,
                mapTypeControl: false,
                scaleControl: false,
                fullscreenControl: false,
                backgroundColor: '#1d1e21'
            });

            this.markerByKey = {};
            this.itemsByMarkerKey = {};
            this.markerMetaByKey = {};
            this.activeMarkerKey = null;
            this.clearSelectedMarkerItems();
            this.resetFilter();

            const itemsByChapterKey = this.items.reduce((groups, item) => {
                const markerKey = this.getMarkerKey(item);

                if (!markerKey) {
                    return groups;
                }

                if (!groups[markerKey]) {
                    groups[markerKey] = [];
                }

                groups[markerKey].push(item);
                return groups;
            }, {});

            const groupedMarkerData = this.chapters.reduce((groups, chapter) => {
                const markerKey = this.getChapterMarkerKey(chapter);
                const lat = parseFloat(chapter.chapterLatitude);
                const lng = parseFloat(chapter.chapterLongitude);
                const chapterItems = markerKey ? (itemsByChapterKey[markerKey] || []) : [];

                if (!markerKey || !Number.isFinite(lat) || !Number.isFinite(lng)) {
                    return groups;
                }

                groups.push({
                    key: markerKey,
                    lat,
                    lng,
                    chapterTitle: chapter.title || '',
                    chapterNumberLabel: this.getChapterNumberLabel(chapter),
                    items: chapterItems,
                    titles: chapterItems
                        .map((item) => (item.title || '').replace(/<[^>]*>/g, '').trim())
                        .filter((title) => title.length)
                });

                return groups;
            }, []);

            if (!groupedMarkerData.length && Number.isFinite(this.mapCenterLat) && Number.isFinite(this.mapCenterLong)) {
                groupedMarkerData.push({
                    key: `${this.mapCenterLat.toFixed(6)},${this.mapCenterLong.toFixed(6)}`,
                    lat: this.mapCenterLat,
                    lng: this.mapCenterLong,
                    items: [],
                    titles: []
                });
            }

            this.itemsByMarkerKey = groupedMarkerData.reduce((groups, markerGroup) => {
                groups[markerGroup.key] = markerGroup.items || [];
                return groups;
            }, {});

            this.markerMetaByKey = groupedMarkerData.reduce((groups, markerGroup) => {
                groups[markerGroup.key] = {
                    chapterTitle: markerGroup.chapterTitle || '',
                    chapterNumberLabel: markerGroup.chapterNumberLabel || ''
                };

                return groups;
            }, {});

            const bounds = new google.maps.LatLngBounds();

            const MarkerOverlay = this.createMarkerOverlayClass();

            groupedMarkerData.forEach((markerGroup) => {
                const position = new google.maps.LatLng(markerGroup.lat, markerGroup.lng);
                const hasAvailableItems = markerGroup.items.length > 0;

                const marker = new MarkerOverlay({
                    position,
                    markerKey: markerGroup.key,
                    hasAvailability: hasAvailableItems,
                    label: hasAvailableItems ? markerGroup.chapterNumberLabel : '',
                    title: markerGroup.chapterTitle || markerGroup.titles.join(' / ')
                });

                marker.setMap(this.map);

                this.markers.push(marker);
                this.markerByKey[markerGroup.key] = marker;

                const markerListener = marker.addListener('click', () => {
                    this.onMarkerClick(markerGroup.key);
                });
                this.markerListeners.push(markerListener);

                bounds.extend(position);
            });

            this.mapClickListener = this.map.addListener('click', () => {
                if (this.isMobileViewport) {
                    this.clearSelectedMarkerItems();
                    return;
                }

                if (this.filteredMarkerKey) {
                    this.resetFilter();
                }
            });

            if (groupedMarkerData.length > 1) {
                this.map.fitBounds(bounds);
            } else if (groupedMarkerData.length === 1) {
                this.map.setCenter(bounds.getCenter());
            }
        },

        intersect() {

            const options = {
                rootMargin: "300px",
                threshold: 0
            };

            const map = document.getElementById("availability-map-container");

            if (!map) {
                return;
            }

            if (typeof IntersectionObserver !== "function") {
                this.injectGoogleMaps();
                return;
            }

            const observerMap = new IntersectionObserver((entries, observer) => {
                const isIntersecting =
                    typeof entries[0].isIntersecting === "boolean"
                        ? entries[0].isIntersecting
                        : entries[0].intersectionRatio > 0;
                if (isIntersecting) {
                    this.injectGoogleMaps();
                    observer.unobserve(map);
                }
            }, options);

            observerMap.observe(map);
        },

        /**
         * Injects the Google Maps scripts when it is not there yet.
         *
         * @return {Void}
         */
        injectGoogleMaps() {
            const tag = document.createElement("script");
            tag.setAttribute("type", "text/javascript");
            tag.defer = true;
            tag.async = true;
            tag.setAttribute(
                "src",
                "//maps.google.com/maps/api/js?key=AIzaSyC7KYVUrV6YAlJSQ7DNQVaNcEz7H3v6o-0&callback=mapsCallback"
            );
            (
                document.getElementsByTagName("head")[0] ||
                document.documentElement
            ).appendChild(tag);
        },

        /**
         * Raised when zoom in button of the map is clicked.
         *
         * @param   {Event}
         * @returns {Void}
         */
        onMapZoomInClick() {
            this.map.setZoom(this.map.getZoom() + 1);
        },

        /**
         * Raised when the zoom out button of the map is clicked.
         *
         * @param {Event} event
         * @returns {Void}
         */
        onMapZoomOutClick() {
            this.map.setZoom(this.map.getZoom() - 1);
        },
    }
});
